(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.StorefrontReliability = api;
})(typeof window === 'undefined' ? globalThis : window, function (root) {
  'use strict';
  const error = code => Object.assign(new Error(code), { code });
  function deadline(work, ms, signal) {
    return new Promise((resolve, reject) => {
      const finish = (fn, value) => { clearTimeout(timer); signal?.removeEventListener('abort', abort); fn(value); };
      const abort = () => finish(reject, error('aborted'));
      const timer = setTimeout(() => finish(reject, error('timeout')), ms);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
      else Promise.resolve().then(work).then(v => finish(resolve, v), e => finish(reject, e));
    });
  }
  async function request(url, { timeout = 5000, signal, type = 'json', ...options } = {}) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    try {
      return await deadline(async () => {
        const response = await root.fetch(url, { ...options, signal: controller.signal });
        if (!response.ok) throw error('http');
        return response[type]();
      }, timeout, signal);
    } finally { controller.abort(); signal?.removeEventListener('abort', abort); }
  }
  function cache(load, { ttl = 300000, backoff = 30000, now = Date.now } = {}) {
    let pending, value, until = 0;
    const get = () => {
      if (pending) return pending;
      if (now() < until) return Promise.resolve(value);
      pending = Promise.resolve().then(load).then(result => { value = result; until = now() + ttl; return value; }, () => { value = null; until = now() + backoff; return null; }).finally(() => { pending = null; });
      return pending;
    };
    get.invalidate = () => { until = 0; };
    return get;
  }
  function latch() {
    let ready = false;
    const listeners = new Set();
    return {
      get ready() { return ready; },
      subscribe(fn) { if (ready) fn(); else listeners.add(fn); return () => listeners.delete(fn); },
      signal() { if (ready) return; ready = true; for (const fn of listeners) { try { fn(); } catch (_) {} } listeners.clear(); }
    };
  }
  const purchasing = latch();
  function originalSource(value, base = root.location?.href) {
    try {
      const processed = new URL(value, base);
      const original = processed.pathname === '/apps/illegal-cutouts/image' ? new URL(processed.searchParams.get('src')) : processed;
      if (original.protocol !== 'https:') return null;
      const own = ['shopillegalshirts.com', 'www.shopillegalshirts.com', 'polandshirts.myshopify.com'];
      return (original.hostname === 'cdn.shopify.com' && original.pathname.startsWith('/s/files/1/0555/6643/4592/')) || (own.includes(original.hostname) && /^\/cdn\/shop\/(files|products)\//.test(original.pathname)) ? original.href : null;
    } catch (_) { return null; }
  }
  async function loadImage(source, { srcset = '', sizes = '', timeout = 7000, Image = root.Image, signal } = {}) {
    const image = new Image();
    try {
      await deadline(() => new Promise((resolve, reject) => {
        const loaded = () => {
          if (!image.naturalWidth) return reject(error('image_error'));
          Promise.resolve().then(() => image.decode?.()).then(() => resolve(image), () => reject(error('image_decode')));
        };
        image.onload = loaded;
        image.onerror = () => reject(error('image_error'));
        if (sizes) image.sizes = sizes;
        if (srcset) image.srcset = srcset;
        image.src = source;
        if (image.complete) loaded();
      }), timeout, signal);
      return image;
    } finally { image.onload = image.onerror = null; }
  }
  const imageJobs = new WeakMap();
  function imageStatus(image, state, label) {
    image.dataset.imageState = state;
    image.setAttribute('aria-busy', String(state === 'loading'));
    let status = image.nextElementSibling?.matches('[data-image-load-status]') ? image.nextElementSibling : null;
    if (!status && image.parentElement && root.document) {
      status = root.document.createElement('span'); status.dataset.imageLoadStatus = ''; status.className = 'image-load-status'; status.setAttribute('role', 'status'); image.after(status);
    }
    if (status) { status.textContent = state === 'loading' ? (label || 'Loading selected image…') : state === 'error' ? (image.complete && image.naturalWidth ? 'Selected image unavailable. Previous photo shown; select again to retry.' : 'Selected image unavailable. Select again to retry.') : ''; status.hidden = state === 'ready'; }
    if (status && image.parentElement?.classList.contains('card-mobile-rail')) {
      status.style.inset = `auto auto 8px ${image.offsetLeft + 8}px`;
      status.style.width = Math.max(0, image.clientWidth - 16) + 'px';
    }
  }
  async function swapImage(image, source, options = {}) {
    const previous = imageJobs.get(image); previous?.abort();
    const controller = new AbortController(); imageJobs.set(image, controller);
    if ((image.currentSrc === source || image.getAttribute('src') === source) && image.complete && image.naturalWidth) { imageStatus(image, 'ready'); return true; }
    imageStatus(image, 'loading', options.label);
    let loaded, usedFallback = false;
    try {
      try { loaded = await loadImage(source, { ...options, signal: controller.signal }); }
      catch (e) {
        if (controller.signal.aborted) return false;
        const fallback = originalSource(options.fallback || source);
        if (!fallback || fallback === source) throw e;
        loaded = await loadImage(fallback, { ...options, srcset: '', signal: controller.signal }); usedFallback = true;
      }
      if (controller.signal.aborted || (options.current && !options.current())) return false;
      const selected = loaded.currentSrc || loaded.src;
      if (options.srcset && !usedFallback) image.srcset = options.srcset; else image.removeAttribute('srcset');
      if (options.sizes) image.sizes = options.sizes;
      image.src = selected; image.alt = options.alt ?? image.alt;
      image.style.opacity = '1'; imageStatus(image, 'ready');
      record(usedFallback ? 'image_fallback' : 'image_success');
      return true;
    } catch (e) {
      if (!controller.signal.aborted) { imageStatus(image, 'error'); record(e.code === 'timeout' ? 'image_timeout' : 'image_error'); }
      return false;
    }
  }
  function colorLink(base, color) {
    const url = new URL(base, root.location?.href || 'https://shopillegalshirts.com');
    url.searchParams.delete('variant'); url.hash = 'color=' + encodeURIComponent(color);
    return url.pathname + url.search + url.hash;
  }
  function variantIntent(product, href, seedId) {
    const url = new URL(href, 'https://shopillegalshirts.com');
    const fragment = new URLSearchParams(url.hash.slice(1));
    const color = fragment.get('color'), browseId = fragment.get('browse-variant');
    const names = product.options.map(o => typeof o === 'string' ? o : o.name);
    const ci = names.findIndex(o => /^(color|colour)$/i.test(o.trim()));
    const explicit = product.variants.find(v => String(v.id) === url.searchParams.get('variant'));
    const colored = color && ci >= 0 && (product.variants.find(v => v.available && v['option' + (ci + 1)] === color) || product.variants.find(v => v['option' + (ci + 1)] === color));
    const browse = browseId && product.variants.find(v => String(v.id) === browseId);
    return { variant: colored || browse || explicit || product.variants.find(v => v.id === seedId) || product.variants.find(v => v.available) || product.variants[0], explicit: !!explicit && !color && !browseId, colorOnly: !!color || !!browseId };
  }
  function browseLink(href) {
    const url = new URL(href, root.location?.href || 'https://shopillegalshirts.com');
    const id = url.searchParams.get('variant');
    if (!id) return href;
    url.searchParams.delete('variant'); url.hash = 'browse-variant=' + encodeURIComponent(id);
    return url.pathname + url.search + url.hash;
  }
  const events = new Set(['essential_ready','essential_unavailable','add_attempt','add_confirmed','add_error','recovery','cart_mismatch','checkout_available','checkout_unavailable','checkout_attempt','checkout_failure','image_success','image_timeout','image_error','image_fallback','search_success','search_timeout','search_fallback','search_failure','filter_request','filter_timeout','filter_applied']);
  const diagnostics = [];
  // Deliberately disabled: activation requires a separately approved destination,
  // consent integration and sampling plan. No identifiers or persistent storage.
  const transport = Object.freeze({ enabled: false, send: () => false });
  const templates = new Set(['index','product','collection','search','cart','page']);
  const clearDiagnostics = () => { diagnostics.length = 0; };
  const permitted = () => root.navigator?.globalPrivacyControl !== true && (!root.Shopify?.customerPrivacy || root.Shopify.customerPrivacy.analyticsProcessingAllowed?.() === true);
  root.document?.addEventListener('visitorConsentCollected', () => { if (!permitted()) clearDiagnostics(); });
  function record(event, duration, code) {
    try {
      if (!permitted()) { clearDiagnostics(); return; }
      if (!events.has(event) || root.IllegalShirts?.reliability_qa !== true) return;
      if (diagnostics.length >= 100) diagnostics.shift();
      diagnostics.push({ event, build: 'reliability-20261015', template: templates.has(root.IllegalShirts.reliability_template) ? root.IllegalShirts.reliability_template : 'other', device: root.innerWidth < 768 ? 'small' : 'large', duration: Number.isFinite(duration) ? Math.min(60000, Math.max(0, Math.round(duration))) : undefined, code: /^(timeout|aborted|http|network|uncertain|rejected|malformed)$/.test(code) ? code : undefined });
    } catch (_) { /* Diagnostics must never interrupt a purchase. */ }
  }
  if (root.document) root.setTimeout(() => { if (!purchasing.ready) record('essential_unavailable', root.performance?.now()); }, 8000);
  return { deadline, request, cache, latch, purchasing, originalSource, loadImage, swapImage, imageStatus, colorLink, browseLink, variantIntent, record, diagnostics, clearDiagnostics, transport };
});
